import { Link } from '@tanstack/react-router';
import { Moon, Search, Sparkles, Sun, SunMoon } from 'lucide-react';
import { useAssistantVisible } from '@/assistant/AssistantHost';
import { toggleAssistant } from '@/assistant/store';
import { useCan } from '@/lib/can';
import { MOD_LABEL } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { setThemePref, useThemePref } from '@/lib/theme';
import { IconButton, Kbd, Logo, useShellLayout } from '@/ui';
import { InboxBell } from './InboxBell';
import { savePreference } from './preferences';
import { ScopeChip, ScopePicker, TimeRange } from './ScopeControls';
import { openSearch } from './searchStore';
import s from './TopBar.module.css';
import { UserMenu } from './UserMenu';

function ThemeToggle() {
  const pref = useThemePref();
  const next = pref === 'system' ? 'light' : pref === 'light' ? 'dark' : 'system';
  const icon = pref === 'light' ? Sun : pref === 'dark' ? Moon : SunMoon;
  const label = pref === 'light' ? t('Theme: light') : pref === 'dark' ? t('Theme: dark') : t('Theme: same as device');
  return (
    <IconButton
      icon={icon}
      label={label}
      onClick={() => {
        setThemePref(next);
        savePreference({ theme: next });
      }}
    />
  );
}

function AssistantToggle() {
  const allowed = useCan('core.ai.use');
  const layout = useShellLayout();
  const visible = useAssistantVisible();
  if (!allowed) return null;
  return <IconButton icon={Sparkles} label={`${t('Assistant')} (${MOD_LABEL}+J)`} active={visible} onClick={() => toggleAssistant(layout === 'ultrawide')} />;
}

export function TopBar() {
  const layout = useShellLayout();
  const phone = layout === 'phone';
  return (
    <header className={s.topbar}>
      {phone ? (
        <>
          <Link to="/" className={s.mark} aria-label={t('Home')}>
            <Logo variant="mark" />
          </Link>
          <ScopeChip />
        </>
      ) : (
        <div className={s.scopeGroup}>
          <ScopePicker />
          <TimeRange />
        </div>
      )}
      <div className={s.spacer} />
      {phone ? (
        <IconButton icon={Search} label={t('Search')} onClick={() => openSearch()} />
      ) : (
        <button type="button" className={s.searchButton} onClick={() => openSearch()}>
          <Search aria-hidden />
          <span className={s.searchText}>{t('Search…')}</span>
          <span className={s.searchKeys}>
            <Kbd>{MOD_LABEL}</Kbd>
            <Kbd>K</Kbd>
          </span>
        </button>
      )}
      <div className={s.actions}>
        {!phone && <InboxBell />}
        <AssistantToggle />
        {!phone && <ThemeToggle />}
        <UserMenu />
      </div>
    </header>
  );
}
